import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';
import { Pagination } from '@components';
import { useBizCardList } from '@hooks';
import BizCardDetailModal from './BizCardDetailModal';
import { post, del, put } from '@utils/axios_api';
import { toast } from 'react-toastify';

function parseJsonField(raw) {
    if (!raw) return null;
    if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
    return raw;
}

function getFieldValue(card, key) {
    if (card[key] != null) return card[key];
    const finalJson = parseJsonField(card.admin_final_json);
    const llmJson   = parseJsonField(card.llm_structured_json);
    const fields    = finalJson?.fields ?? llmJson?.fields ?? [];
    return fields.find((f) => f?.key === key)?.value || null;
}

const STATUS_OPTIONS = [
    { value: '',               label: '전체' },
    { value: 'pending_review', label: '검수 대기' },
    { value: 'completed',      label: '확정 완료' },
    { value: 'ocr_failed',     label: '오류' },
];

const STATUS_STYLE = {
    pending_review: { background: '#fef9c3', color: '#92400e' },
    completed:      { background: '#dcfce7', color: '#166534' },
    ocr_failed:     { background: '#fee2e2', color: '#991b1b' },
    uploaded:       { background: '#f3f4f6', color: '#4b5563' },
    ocr_completed:  { background: '#dbeafe', color: '#1d4ed8' },
};

const STATUS_LABEL = {
    pending_review: '검수 대기',
    completed:      '확정 완료',
    ocr_failed:     'OCR 오류',
    uploaded:       '업로드됨',
    ocr_completed:  'OCR 완료',
};

/* ── 그룹 사이드바 아이템 ── */
function GroupItem({ label, active, onClick, onEdit, onDelete }) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 14px',
                cursor: 'pointer',
                background: active ? '#eff6ff' : hovered ? '#f9fafb' : 'transparent',
                borderLeft: `3px solid ${active ? '#3b82f6' : 'transparent'}`,
            }}
        >
            <span style={{
                flex: 1,
                fontSize: '13px',
                fontWeight: active ? '600' : '400',
                color: active ? '#1d4ed8' : '#374151',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
                {label}
            </span>
            {hovered && onEdit && (
                <div style={{ display: 'flex', gap: '2px' }}>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        title="이름 수정"
                        style={{ padding: '2px 5px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '13px', lineHeight: 1 }}
                    >✎</button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        title="그룹 삭제"
                        style={{ padding: '2px 5px', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '13px', lineHeight: 1 }}
                    >✕</button>
                </div>
            )}
        </div>
    );
}

/* ── 메인 컴포넌트 ── */
export function BizCardList() {
    const navigate = useNavigate();
    const {
        methods, cardList, totalCount, totalPages,
        pageIndex, recordCountPerPage,
        handlePageChange, changePageSize,
        isLoading, isError, onSubmit, isHealthError,
        selectedItems, isAllChecked, handleSelectAll, handleSelectItem,
        handleDelete, isDeleting,
        groups, refetchGroups, selectedGroupId, handleGroupChange,
        viewTab, handleTabChange, handleUnsave,
    } = useBizCardList();
    const { register, handleSubmit } = methods;

    const [detailCardId, setDetailCardId] = useState(null);
    const [showAddGroup, setShowAddGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [editingGroup, setEditingGroup] = useState(null); // { id, name }
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const firstIndex = (pageIndex - 1) * recordCountPerPage;

    useEffect(() => {
        const fn = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) return;
        try {
            await post('/biz/groups', { groupName: newGroupName.trim() });
            setNewGroupName('');
            setShowAddGroup(false);
            refetchGroups();
            toast.success('그룹이 생성되었습니다.');
        } catch {
            toast.error('그룹 생성에 실패했습니다.');
        }
    };

    const handleRenameGroup = async (id) => {
        if (!editingGroup?.name?.trim()) return;
        try {
            await put(`/biz/groups/${id}`, { groupName: editingGroup.name.trim() });
            setEditingGroup(null);
            refetchGroups();
            toast.success('그룹 이름이 변경되었습니다.');
        } catch {
            toast.error('이름 변경에 실패했습니다.');
        }
    };

    const handleDeleteGroup = async (id) => {
        if (!window.confirm('그룹을 삭제하시겠습니까?\n명함은 삭제되지 않습니다.')) return;
        try {
            await del(`/biz/groups/${id}`);
            if (selectedGroupId === id) handleGroupChange(null);
            refetchGroups();
            toast.success('그룹이 삭제되었습니다.');
        } catch {
            toast.error('그룹 삭제에 실패했습니다.');
        }
    };

    return (
        <>
        <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
        <div className="contents-wrap">

            <div className="page-title-wrap">
                <h2><span className="icon-dot-circle"></span> 명함 관리</h2>
            </div>

            {isHealthError && (
                <div style={{ background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: '6px', padding: '8px 16px', marginBottom: '12px', fontSize: '13px', color: '#b91c1c' }}>
                    BizScanPro 서버에 연결할 수 없습니다. 업로드 및 분석 기능이 동작하지 않을 수 있습니다.
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', alignItems: 'flex-start' }}>

                {/* ── 그룹: 모바일 드롭다운 / 데스크탑 사이드바 ── */}
                {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>그룹</label>
                            <select
                                value={selectedGroupId ?? ''}
                                onChange={(e) => handleGroupChange(e.target.value ? Number(e.target.value) : null)}
                                style={{ fontSize: '13px', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', color: '#374151', flex: 1, minWidth: 0 }}
                            >
                                <option value="">전체</option>
                                {groups.map((g) => <option key={g.id} value={g.id}>{g.groupName}</option>)}
                            </select>
                            {selectedGroupId != null && (
                                <button
                                    type="button"
                                    onClick={() => handleDeleteGroup(selectedGroupId)}
                                    title="선택 그룹 삭제"
                                    style={{ flexShrink: 0, padding: '5px 9px', fontSize: '13px', border: '1px solid #fca5a5', borderRadius: '6px', background: '#fff', color: '#dc2626', cursor: 'pointer' }}
                                >삭제</button>
                            )}
                            <button
                                type="button"
                                onClick={() => { setShowAddGroup((v) => !v); setNewGroupName(''); }}
                                style={{ flexShrink: 0, padding: '5px 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: '600' }}
                            >+ 추가</button>
                        </div>
                        {showAddGroup && (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <input
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter')  handleCreateGroup();
                                        if (e.key === 'Escape') { setShowAddGroup(false); setNewGroupName(''); }
                                    }}
                                    placeholder="그룹 이름 입력"
                                    autoFocus
                                    style={{ flex: 1, fontSize: '13px', padding: '5px 8px', border: '1px solid #3b82f6', borderRadius: '6px', outline: 'none' }}
                                />
                                <button type="button" onClick={handleCreateGroup} style={{ flexShrink: 0, padding: '5px 12px', fontSize: '13px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>확인</button>
                                <button type="button" onClick={() => { setShowAddGroup(false); setNewGroupName(''); }} style={{ flexShrink: 0, padding: '5px 10px', fontSize: '13px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}>취소</button>
                            </div>
                        )}
                    </div>
                ) : (
                <div style={{
                    width: '190px',
                    flexShrink: 0,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    paddingBottom: '6px',
                }}>
                    <div style={{ padding: '11px 14px 9px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #f3f4f6' }}>
                        그룹
                    </div>

                    <GroupItem
                        label="전체"
                        active={selectedGroupId == null}
                        onClick={() => handleGroupChange(null)}
                    />

                    {groups.map((g) => (
                        <div key={g.id}>
                            {editingGroup?.id === g.id ? (
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '5px 10px' }}>
                                    <input
                                        value={editingGroup.name}
                                        onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter')  handleRenameGroup(g.id);
                                            if (e.key === 'Escape') setEditingGroup(null);
                                        }}
                                        autoFocus
                                        style={{ flex: 1, fontSize: '12px', padding: '3px 6px', border: '1px solid #3b82f6', borderRadius: '4px', outline: 'none', minWidth: 0 }}
                                    />
                                    <button type="button" onClick={() => handleRenameGroup(g.id)} style={{ fontSize: '11px', padding: '3px 7px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', whiteSpace: 'nowrap' }}>저장</button>
                                    <button type="button" onClick={() => setEditingGroup(null)} style={{ fontSize: '11px', padding: '3px 7px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>✕</button>
                                </div>
                            ) : (
                                <GroupItem
                                    label={g.groupName}
                                    active={selectedGroupId === g.id}
                                    onClick={() => handleGroupChange(g.id)}
                                    onEdit={() => setEditingGroup({ id: g.id, name: g.groupName })}
                                    onDelete={() => handleDeleteGroup(g.id)}
                                />
                            )}
                        </div>
                    ))}

                    <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '6px', padding: '6px 10px 0' }}>
                        {showAddGroup ? (
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter')  handleCreateGroup();
                                        if (e.key === 'Escape') { setShowAddGroup(false); setNewGroupName(''); }
                                    }}
                                    placeholder="그룹 이름"
                                    autoFocus
                                    style={{ flex: 1, fontSize: '12px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', outline: 'none', minWidth: 0 }}
                                />
                                <button type="button" onClick={handleCreateGroup} style={{ fontSize: '11px', padding: '3px 7px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', whiteSpace: 'nowrap' }}>추가</button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowAddGroup(true)}
                                style={{ width: '100%', textAlign: 'left', padding: '5px 4px', fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                            >
                                <span style={{ fontSize: '15px', lineHeight: 1, color: '#9ca3af' }}>+</span> 그룹 추가
                            </button>
                        )}
                    </div>
                </div>
                )}

                {/* ── 메인 콘텐츠 ── */}
                <div style={{ flex: 1, minWidth: 0 }}>

                    <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        {[
                            { key: 'mine',  label: '내 명함' },
                            { key: 'saved', label: '받은 명함' },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => handleTabChange(tab.key)}
                                style={{
                                    padding: '9px 18px',
                                    fontSize: '14px',
                                    fontWeight: viewTab === tab.key ? '600' : '400',
                                    color: viewTab === tab.key ? '#1d4ed8' : '#6b7280',
                                    background: 'none',
                                    border: 'none',
                                    borderBottom: viewTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
                                    cursor: 'pointer',
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* 검색 영역 */}
                    <div className="search-wrap">
                        <div className="search-row" style={isMobile ? { flexDirection: 'column', alignItems: 'stretch', gap: '8px' } : {}}>
                            <div className="search-item" style={isMobile ? { flexWrap: 'wrap', gap: '8px' } : {}}>
                                {viewTab === 'mine' && (
                                    <>
                                        <label>검수 상태</label>
                                        <select {...register('status')} className="input-select">
                                            {STATUS_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </>
                                )}
                                <label style={{ marginLeft: !isMobile && viewTab === 'mine' ? '16px' : 0 }}>이름/회사명</label>
                                <input
                                    {...register('q')}
                                    placeholder="검색어를 입력하세요"
                                    className="input-search"
                                    style={{ width: isMobile ? '100%' : '200px' }}
                                />
                            </div>
                            <div className="search-btn-container" style={isMobile ? { display: 'flex' } : {}}>
                                <button type="submit" className="btn-search" style={isMobile ? { flex: 1 } : {}}>조회</button>
                            </div>
                        </div>
                    </div>

                    <section>
                        <div className="section-tit-wrap">
                            <h3 className="border-left-primary">
                                &nbsp;명함 목록
                                <span className="total-txt"> | 총 <span className="red">{totalCount}</span> 건</span>
                            </h3>
                            <div className="btn-right-box">
                                <ul>
                                    {viewTab === 'mine' && (
                                        <li>
                                            <button type="button" className="btn-delete" disabled={isDeleting} onClick={handleDelete}>
                                                삭제
                                            </button>
                                        </li>
                                    )}
                                    <li>
                                        <button type="button" className="btn-save" onClick={() => navigate('/biz/card-upload')}>
                                            명함 업로드
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* ── 모바일 카드 뷰 ── */}
                        {isMobile ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {isLoading && (
                                    <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '24px 0' }}>로딩 중...</p>
                                )}
                                {isError && (
                                    <p style={{ textAlign: 'center', color: '#b91c1c', fontSize: '13px', padding: '24px 0' }}>데이터를 불러오지 못했습니다.</p>
                                )}
                                {!isLoading && !isError && cardList.length === 0 && (
                                    <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', padding: '24px 0' }}>
                                        {viewTab === 'mine' ? '등록된 명함이 없습니다.' : '받은 명함이 없습니다.'}
                                    </p>
                                )}
                                {cardList.map((card, idx) => (
                                    <div
                                        key={card.id}
                                        onClick={() => setDetailCardId(card.id)}
                                        style={{
                                            background: '#fff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            padding: '12px 14px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '10px',
                                        }}
                                    >
                                        {viewTab === 'mine' && (
                                            <div onClick={(e) => e.stopPropagation()} style={{ paddingTop: '2px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.includes(card.id)}
                                                    onChange={() => handleSelectItem(card.id)}
                                                />
                                            </div>
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {card.person_name || '-'}
                                                </span>
                                                {viewTab === 'mine' ? (
                                                    <span style={{
                                                        flexShrink: 0,
                                                        padding: '2px 8px',
                                                        borderRadius: '999px',
                                                        fontSize: '11px',
                                                        fontWeight: '500',
                                                        ...(STATUS_STYLE[card.status] || { background: '#f3f4f6', color: '#4b5563' }),
                                                    }}>
                                                        {STATUS_LABEL[card.status] || card.status || '-'}
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); handleUnsave(card.id); }}
                                                        style={{ flexShrink: 0, fontSize: '11px', padding: '2px 8px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                                                    >
                                                        저장 해제
                                                    </button>
                                                )}
                                            </div>
                                            <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {[card.company_name, getFieldValue(card, 'job_title')].filter(Boolean).join(' · ') || '-'}
                                            </p>
                                            {(card.email || getFieldValue(card, 'email') || (viewTab === 'saved' && (card.phone || getFieldValue(card, 'phone')))) && (
                                                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {viewTab === 'saved'
                                                        ? (card.phone || getFieldValue(card, 'phone') || card.email || getFieldValue(card, 'email'))
                                                        : (card.email || getFieldValue(card, 'email'))
                                                    }
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                        /* ── 데스크탑 테이블 ── */
                        <div className="table-container overflow-x-auto table-list-wrapper">
                            <table className="table-list" style={{ width: '100%', tableLayout: 'fixed' }}>
                                <colgroup>
                                    {viewTab === 'mine' && <col style={{ width: '4%' }} />}
                                    <col style={{ width: '5%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: viewTab === 'saved' ? '19%' : '14%' }} />
                                    {viewTab === 'mine' && <col style={{ width: '9%' }} />}
                                    <col style={{ width: '8%' }} />
                                    {viewTab === 'saved' && <col style={{ width: '13%' }} />}
                                    <col style={{ width: viewTab === 'saved' ? '20%' : '14%' }} />
                                    {viewTab === 'mine' && <col style={{ width: '6%' }} />}
                                    {viewTab === 'mine' && <col style={{ width: '6%' }} />}
                                    {viewTab === 'mine' && <col style={{ width: '7%' }} />}
                                    {viewTab === 'mine' && <col style={{ width: '6%' }} />}
                                    {viewTab === 'saved' && <col style={{ width: '9%' }} />}
                                </colgroup>
                                <thead>
                                    <tr>
                                        {viewTab === 'mine' && (
                                            <th><input type="checkbox" checked={isAllChecked} onChange={handleSelectAll} /></th>
                                        )}
                                        <th>번호</th>
                                        <th>이름</th>
                                        <th style={{ textAlign: 'left' }}>회사명</th>
                                        {viewTab === 'mine' && <th>검수 상태</th>}
                                        <th>직책</th>
                                        {viewTab === 'saved' && <th>전화번호</th>}
                                        <th style={{ textAlign: 'left' }}>이메일</th>
                                        {viewTab === 'mine' && <th title="LLM 요청 여부">LLM 요청</th>}
                                        {viewTab === 'mine' && <th title="LLM 실제 호출 여부">LLM 호출</th>}
                                        {viewTab === 'mine' && <th title="LLM 요청 모드">모드</th>}
                                        {viewTab === 'mine' && <th title="LLM 성공 여부">성공</th>}
                                        {viewTab === 'saved' && <th>관리</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading && (
                                        <tr><td colSpan={viewTab === 'mine' ? 11 : 7} className="table-empty" style={{ textAlign: 'center' }}>로딩 중...</td></tr>
                                    )}
                                    {isError && (
                                        <tr><td colSpan={viewTab === 'mine' ? 11 : 7} className="table-empty" style={{ textAlign: 'center', color: '#b91c1c' }}>데이터를 불러오지 못했습니다.</td></tr>
                                    )}
                                    {!isLoading && !isError && cardList.length === 0 && (
                                        <tr>
                                            <td colSpan={viewTab === 'mine' ? 11 : 7} className="table-empty" style={{ textAlign: 'center' }}>
                                                {viewTab === 'mine' ? '등록된 명함이 없습니다.' : '받은 명함이 없습니다.'}
                                            </td>
                                        </tr>
                                    )}
                                    {cardList.map((card, idx) => (
                                        <tr key={card.id} onClick={() => setDetailCardId(card.id)} style={{ cursor: 'pointer' }}>
                                            {viewTab === 'mine' && (
                                                <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                                                    <input type="checkbox" checked={selectedItems.includes(card.id)} onChange={() => handleSelectItem(card.id)} />
                                                </td>
                                            )}
                                            <td style={{ textAlign: 'center' }}>{firstIndex + idx + 1}</td>
                                            <td style={{ textAlign: 'center' }}>{card.person_name || '-'}</td>
                                            <td style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.company_name || '-'}</td>
                                            {viewTab === 'mine' && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '500', ...(STATUS_STYLE[card.status] || { background: '#f3f4f6', color: '#4b5563' }) }}>
                                                        {STATUS_LABEL[card.status] || card.status || '-'}
                                                    </span>
                                                </td>
                                            )}
                                            <td style={{ textAlign: 'center' }}>{getFieldValue(card, 'job_title') || '-'}</td>
                                            {viewTab === 'saved' && (
                                                <td style={{ textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {card.phone || getFieldValue(card, 'phone') || '-'}
                                                </td>
                                            )}
                                            <td style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {card.email || getFieldValue(card, 'email') || '-'}
                                            </td>
                                            {viewTab === 'mine' && (
                                                <td style={{ textAlign: 'center' }}>
                                                    {card.llm_enabled_for_request == null ? '-' : card.llm_enabled_for_request ? <span style={{ color: '#1d4ed8' }}>✓</span> : <span style={{ color: '#9ca3af' }}>✗</span>}
                                                </td>
                                            )}
                                            {viewTab === 'mine' && (
                                                <td style={{ textAlign: 'center' }}>
                                                    {card.llm_called == null ? '-' : card.llm_called ? <span style={{ color: '#1d4ed8' }}>✓</span> : <span style={{ color: '#9ca3af' }}>✗</span>}
                                                </td>
                                            )}
                                            {viewTab === 'mine' && (
                                                <td style={{ textAlign: 'center', fontSize: '11px', color: '#6b7280' }}>{card.llm_request_mode || '-'}</td>
                                            )}
                                            {viewTab === 'mine' && (
                                                <td style={{ textAlign: 'center' }}>
                                                    {card.llm_call_success == null ? '-' : card.llm_call_success ? <span style={{ color: '#166534' }}>✓</span> : <span style={{ color: '#dc2626' }}>✗</span>}
                                                </td>
                                            )}
                                            {viewTab === 'saved' && (
                                                <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleUnsave(card.id)}
                                                        style={{ fontSize: '12px', padding: '3px 9px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                                                    >
                                                        저장 해제
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                            <span style={{ fontSize: '14px', whiteSpace: 'nowrap', color: '#555' }}>페이지당 행 수</span>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 'auto' }}
                                value={recordCountPerPage}
                                onChange={(e) => changePageSize(Number(e.target.value))}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>

                        {totalCount > 0 && (
                            <Pagination
                                totalPages={totalPages}
                                currentPage={pageIndex}
                                onPageChange={handlePageChange}
                                pageRangeDisplayed={10}
                            />
                        )}
                    </section>
                </div>

            </div>
        </div>
        </form>
        </FormProvider>

        {detailCardId && (
            <BizCardDetailModal
                cardId={detailCardId}
                groups={groups}
                onClose={() => setDetailCardId(null)}
                onReview={(id) => navigate(`/biz/card-review/${id}`)}
                onGroupsChanged={refetchGroups}
            />
        )}
        </>
    );
}
